import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button } from "@postdaily/ui/components/button";
import { Spinner } from "@postdaily/ui/components/spinner";
import {
  createFileRoute,
  Outlet,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { useAuthRedirect } from "@/hooks/use-auth-redirect";
import { useRunOnce } from "@/hooks/use-run-once";
import { authClient } from "@/lib/auth-client";
import { createNoIndexHead } from "@/lib/seo";

export const Route = createFileRoute("/(auth)")({
  // Auth surfaces don't need SEO; keep them client-only like the dashboard.
  ssr: false,
  head: () => createNoIndexHead("Sign in — PostDaily"),
  component: RouteComponent,
});

// Dark indigo ambient background — the auth surface is a fixed brand moment, so
// it is scoped to dark (`.dark`) regardless of the user's app theme.
const authBackground =
  "radial-gradient(1100px 620px at 78% -8%, oklch(0.42 0.14 268 / 0.32), transparent 60%)," +
  "radial-gradient(900px 600px at 12% 110%, oklch(0.36 0.12 282 / 0.22), transparent 55%)," +
  "oklch(0.14 0.012 264)";

/** This layout's own pages; anything else is a navigation on its way out. */
const authPaths = new Set(["/login", "/forgot-password", "/two-factor"]);

function RouteComponent() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const redirectTo = useAuthRedirect();
  const { data: session, isPending, error, refetch } = authClient.useSession();
  /**
   * Whether the session check has ever finished.
   *
   * `isPending` is true for background refetches too, not just the first load,
   * and swapping the outlet for a spinner UNMOUNTS whatever is under it. Any
   * call that touches the session — sending a 2FA code, for one — therefore
   * tore the page down mid-interaction and remounted it with fresh state, which
   * silently discarded a chosen verification method or a half-typed code.
   *
   * So the spinner is for the first check only; a later refetch leaves the page
   * standing.
   */
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    if (!isPending) {
      setSessionChecked(true);
    }
  }, [isPending]);

  let content: ReactNode;

  if (session && pathname !== "/reset-password") {
    // Once the URL has left this layout's pages, a navigation is already in
    // flight (the sign-in form starts its own) and the new URL carries no
    // `?redirect=`: redirecting from here would send it to /queue instead.
    content = authPaths.has(pathname) ? (
      <PostAuthRedirect to={redirectTo ?? "/queue"} />
    ) : (
      <Spinner className="size-10" />
    );
  } else if (isPending && !sessionChecked) {
    content = <Spinner className="size-10" />;
  } else if (error) {
    content = (
      <Alert variant="destructive" className="max-w-md">
        <AlertTitle>Could not check your session</AlertTitle>
        <AlertDescription>
          We could not confirm your sign-in state. Try again to continue.
        </AlertDescription>
        <AlertAction>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              void refetch();
            }}
          >
            Try again
          </Button>
        </AlertAction>
      </Alert>
    );
  } else {
    content = <Outlet />;
  }

  return (
    <main
      className="dark flex min-h-svh items-center justify-center p-6 text-foreground"
      style={{ background: authBackground }}
    >
      {content}
    </main>
  );
}

/**
 * Typed routes can't express arbitrary return paths; history.replace can.
 *
 * Navigates ONCE, to the first target. Replacing the URL re-renders this
 * layout before it unmounts, and the new URL has no `?redirect=` — so `to`
 * turns into "/queue" and an effect keyed on it navigated a second time,
 * overriding the redirect. Every signed-in `?redirect=` (the AI-agent consent
 * page first among them) lost to that race and landed on /queue or onboarding.
 */
function PostAuthRedirect({ to }: { to: string }) {
  const router = useRouter();
  const [target] = useState(to);

  useRunOnce(true, () => {
    if (target === "/queue") {
      void router.navigate({ to: "/queue" });
      return;
    }
    router.history.replace(target);
  });

  return <Spinner className="size-10" />;
}
